/**
 * Web-only persistence for the admin session — copied verbatim from
 * `apps/admin-web/src/auth/authStorage.ts` (which itself mirrors `apps/admin/ui/
 * authStorage.web.ts`, falling back to `localStorage` since expo-secure-store has no web
 * implementation). No native counterpart exists here — this app is web-only (docs/325/337).
 *
 * **Two persistence tiers (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 defect: "`localStorage`
 * for the admin key"):** the sign-in MODE and, in password mode, the session bearer token +
 * cached user identity are a normal signed-in session — persisted here in `localStorage` so a
 * reload/re-open doesn't force a re-login, unchanged from before. The shared break-glass
 * `X-Admin-Key`, however, is a STATIC secret every admin who knows it can use to act as an
 * admin indefinitely — leaving it in `localStorage` means it survives across browser restarts
 * forever and is readable by any script that ever runs an XSS on this origin, long after the
 * admin who typed it in has closed the tab. `adminKeyStorage` below persists it in
 * `sessionStorage` instead: cleared the moment the tab/window closes, and per-tab (never
 * silently shared across tabs the way `localStorage` is) — it's still just as XSS-readable
 * *while the tab is open* (neither storage is a security boundary against live script
 * injection; only an `httpOnly` cookie would be, and this is a plain SPA fetch header, not a
 * cookie), but it no longer persists indefinitely once the tab is gone.
 */
import { DEFAULT_ADMIN_PROVIDER_ID } from "../config/adminDeployment.config";

const PREFIX = "equipped-admin-web-mantis:";

function providerPrefix(providerId: string): string {
  // Preserve today's exact single-provider keys so existing sessions survive
  // the provider-scoping refactor. Every non-default provider gets an isolated
  // encoded namespace.
  return providerId === DEFAULT_ADMIN_PROVIDER_ID ? PREFIX : `${PREFIX}${encodeURIComponent(providerId)}:`;
}

function makeStorage(providerId: string, backing: () => Storage) {
  const prefix = providerPrefix(providerId);
  return {
    getItem(key: string): string | null {
      try {
        return backing().getItem(prefix + key);
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): void {
      try {
        backing().setItem(prefix + key, value);
      } catch {
        /* private-browsing / storage-full — non-fatal, matches the RN fallback */
      }
    },
    removeItem(key: string): void {
      try {
        backing().removeItem(prefix + key);
      } catch {
        /* noop */
      }
    },
  };
}

/** Creates both auth tiers in a provider-specific namespace. */
export function createProviderAuthStorage(providerId: string) {
  return {
    /** Mode + password-session token/user — a normal persisted sign-in. */
    authStorage: makeStorage(providerId, () => window.localStorage),
    /** Shared break-glass key only — scoped to this provider and browser tab. */
    adminKeyStorage: makeStorage(providerId, () => window.sessionStorage),
  };
}
